import { normalizeIndianPhone } from "./phone";

export const DEFAULT_PHONE_PREFIX = "";
export const CONTACT_ROLE_OPTIONS = [
  "Accounts",
  "Purchase",
  "Our Contact Person",
  "Plant Engineer",
  "Shift Engineer",
  "Supervisor",
  "Technician",
  "Other"
];
const ADDRESS_FIELDS = ["street", "area", "city", "state", "pincode"];

const normalizeAddressValue = (value = "") => String(value || "").trim();

export const ensurePhonePrefix = (value = "") => {
  return String(value || "").trimStart();
};

export const createEmptyContact = (isPrimary = false) => ({
  role: "Our Contact Person",
  name: "",
  mobileNumber: DEFAULT_PHONE_PREFIX,
  email: "",
  whatsappNumber: "",
  remarks: "",
  isPrimary
});

export const normalizeContactRole = (role = "") => {
  const normalized = String(role || "").trim();
  return CONTACT_ROLE_OPTIONS.includes(normalized) ? normalized : "Other";
};

export const normalizeCustomerContact = (contact = {}, index = 0) => ({
  role: normalizeContactRole(contact.role),
  name: String(contact.name || "").trim(),
  mobileNumber: normalizeIndianPhone(contact.mobileNumber || contact.phone || contact.mobile || ""),
  email: String(contact.email || "").trim(),
  whatsappNumber: normalizeIndianPhone(contact.whatsappNumber || contact.whatsapp || ""),
  remarks: String(contact.remarks || "").trim(),
  isPrimary: Boolean(contact.isPrimary || contact.primary || contact.primaryContact || index === 0)
});

export const getCustomerContacts = (customer = {}) => {
  const safeCustomer = customer || {};
  return Array.isArray(safeCustomer.additionalContacts)
    ? safeCustomer.additionalContacts.map(normalizeCustomerContact)
    : [];
};

export const getPrimaryCustomerContact = (customer = {}) => {
  const safeCustomer = customer || {};
  const contacts = getCustomerContacts(safeCustomer).filter((contact) =>
    contact.name || contact.mobileNumber || contact.email || contact.whatsappNumber || contact.remarks
  );

  if (contacts.length === 0) {
    const fallback = createEmptyContact(true);
    fallback.name = String(safeCustomer.contactPerson || "").trim();
    fallback.mobileNumber = normalizeIndianPhone(safeCustomer.phone || "");
    fallback.email = String(safeCustomer.email || "").trim();
    fallback.isPrimary = Boolean(fallback.name || fallback.mobileNumber || fallback.email);
    return fallback.isPrimary ? fallback : null;
  }

  return contacts.find((contact) => contact.isPrimary) || { ...contacts[0], isPrimary: true };
};

export const createEmptyCustomerForm = () => ({
  name: "",
  supplierCode: "",
  email: "",
  gstNumber: "",
  gstType: "cgst_sgst",
  lutRefNo: "",
  billingAddress: {
    street: "",
    area: "",
    city: "",
    state: "",
    pincode: ""
  },
  address: {
    street: "",
    area: "",
    city: "",
    state: "",
    pincode: ""
  },
  contactPerson: "",
  phone: DEFAULT_PHONE_PREFIX,
  additionalContacts: [createEmptyContact(true)],
  googleMapsLink: "",
  additionalInfo: "",
  groupId: ""
});

export const cloneAddress = (address = {}) => ({
  street: String(address?.street || ""),
  area: String(address?.area || ""),
  city: String(address?.city || ""),
  state: String(address?.state || ""),
  pincode: String(address?.pincode || "")
});

export const areAddressesEqual = (left = {}, right = {}) =>
  ADDRESS_FIELDS.every(
    (field) => normalizeAddressValue(left?.[field]) === normalizeAddressValue(right?.[field])
  );

export const getWorkAddress = (customer = {}) => {
  const workAddress = cloneAddress(customer.address || {});
  const billingAddress = cloneAddress(customer.billingAddress || {});
  const hasWorkAddress = ADDRESS_FIELDS.some((field) => normalizeAddressValue(workAddress[field]));

  return hasWorkAddress ? workAddress : billingAddress;
};

export const mapCustomerToForm = (customer = {}) => {
  const safeCustomer = customer || {};
  const empty = createEmptyCustomerForm();
  const primaryContact = getPrimaryCustomerContact(safeCustomer);
  const normalizedContacts = getCustomerContacts(safeCustomer);

  const gst = safeCustomer.gstNumber !== undefined && safeCustomer.gstNumber !== null
    ? safeCustomer.gstNumber
    : (safeCustomer.additionalDetails && safeCustomer.additionalDetails.gstNumber) || "";

  return {
    ...empty,
    name: safeCustomer.name || "",
    supplierCode: safeCustomer.supplierCode || "",
    email: safeCustomer.email || "",
    gstNumber: gst,
    gstType: safeCustomer.gstType || "cgst_sgst",
    lutRefNo: safeCustomer.lutRefNo || "",
    billingAddress: {
      ...empty.billingAddress,
      ...(safeCustomer.billingAddress || safeCustomer.address || {})
    },
    address: {
      ...empty.address,
      ...(safeCustomer.address || safeCustomer.billingAddress || {})
    },
    contactPerson: primaryContact?.name || safeCustomer.contactPerson || "",
    phone: primaryContact?.mobileNumber || normalizeIndianPhone(safeCustomer.phone || ""),
    additionalContacts: normalizedContacts.length > 0 ? normalizedContacts : empty.additionalContacts,
    googleMapsLink: safeCustomer.googleMapsLink || "",
    additionalInfo: safeCustomer.additionalInfo || "",
    groupId: safeCustomer.groupId?._id || safeCustomer.groupId || ""
  };
};

export const sanitizeCustomerPayload = (form = {}) => {
  // Build the API payload directly from the form — do NOT route through
  // mapCustomerToForm which is an API-response → form mapper (wrong direction).
  const normalizedContacts = Array.isArray(form.additionalContacts)
    ? form.additionalContacts
        .map((contact, index) => normalizeCustomerContact(contact, index))
        .filter((contact) =>
          contact.name || contact.mobileNumber || contact.email || contact.whatsappNumber || contact.remarks
        )
    : [];

  const primaryContact = normalizedContacts.find((contact) => contact.isPrimary) || normalizedContacts[0] || null;
  const contactPerson = String(primaryContact?.name || form.contactPerson || "").trim();
  const phone = normalizeIndianPhone(primaryContact?.mobileNumber || form.phone || "");
  const email = String(form.email || primaryContact?.email || "").trim();

  const contactsWithPrimary = normalizedContacts.length > 0
    ? normalizedContacts.map((contact, index) => ({
        ...contact,
        isPrimary: index === 0 ? Boolean(primaryContact) && (contact.isPrimary || contact === primaryContact) : contact.isPrimary
      }))
    : (contactPerson || phone || email)
      ? [{
          ...createEmptyContact(true),
          role: "Our Contact Person",
          name: contactPerson,
          mobileNumber: phone,
          email,
          isPrimary: true
        }]
      : [];

  return {
    name: String(form.name || "").trim(),
    supplierCode: String(form.supplierCode || "").trim(),
    email: String(form.email || "").trim(),
    gstNumber: String(form.gstNumber || "").trim(),
    gstType: String(form.gstType || "cgst_sgst").trim() || "cgst_sgst",
    lutRefNo: String(form.gstType || "").trim().toLowerCase() === "sez_supply"
      ? String(form.lutRefNo || "").trim()
      : "",
    contactPerson,
    phone,
    billingAddress: {
      street: String(form.billingAddress?.street || "").trim(),
      area: String(form.billingAddress?.area || "").trim(),
      city: String(form.billingAddress?.city || "").trim(),
      state: String(form.billingAddress?.state || "").trim(),
      pincode: String(form.billingAddress?.pincode || "").trim()
    },
    address: {
      street: String(form.address?.street || "").trim(),
      area: String(form.address?.area || "").trim(),
      city: String(form.address?.city || "").trim(),
      state: String(form.address?.state || "").trim(),
      pincode: String(form.address?.pincode || "").trim()
    },
    googleMapsLink: String(form.googleMapsLink || "").trim(),
    additionalContacts: contactsWithPrimary,
    additionalInfo: String(form.additionalInfo || "").trim(),
    groupId: form.groupId ? String(form.groupId).trim() : null
  };
};
